'use client';
import React from 'react';
import { usePathname } from 'next/navigation';

import { OpeningHours } from '../opening-hours/opening-hours';
import { CrowdIndicator } from './crowd-indicator';
import {
  useGetLibraryConstantsDictionaryData,
  useTenantConfig,
} from '@/providers/ConfigProvider';
import LinkWrapper from '../link-wrapper/link-wrapper';
import { useCrowdLevel } from '@/context/CrowdLevelContext';
import { useIsLocationOpen } from '@/hooks/use-is-location-open';

export interface HeaderOpeningHoursProps {
  meetingPlace: any;
  openingHoursLink?: any;
  showMobileCrowd?: boolean;
}

export const HeaderOpeningHours = ({
  meetingPlace,
  openingHoursLink,
  showMobileCrowd = true,
}: HeaderOpeningHoursProps) => {
  const dictionary = useGetLibraryConstantsDictionaryData();
  const crowdLevel = useCrowdLevel();
  const pathname = usePathname();
  const config = useTenantConfig();
  const showCrowdIndicator =
    config?.NEXT_PUBLIC_SHOW_CROWD_INDICATOR === 'true';
  const locale = meetingPlace?.sys?.locale?.toLowerCase();
  const normalizedPath = pathname?.replace(/\/$/, '').toLowerCase() ?? '';
  const isHomePage = normalizedPath === `/${locale}`;

  // Check if location is currently open using same logic as OpeningHours component
  const isOpen = useIsLocationOpen(
    meetingPlace?.openingHours,
    meetingPlace?.openingHoursExceptions,
    meetingPlace?.timezone,
    meetingPlace?.sys?.locale,
  );

  return (
    <>
      {/* Mobile: crowd pill — only on non-homepage pages, and not inside the fold-out menu */}
      {showCrowdIndicator &&
        crowdLevel !== null &&
        !isHomePage &&
        showMobileCrowd && (
          <div className="flex items-center lg:hidden">
            {openingHoursLink ? (
              <LinkWrapper data={openingHoursLink} className="block">
                <CrowdIndicator
                  level={crowdLevel}
                  maxLevel={4}
                  showChevron
                  crowdLabel={dictionary?.crowd ?? 'CROWD'}
                  isOpen={isOpen}
                />
              </LinkWrapper>
            ) : (
              <CrowdIndicator
                level={crowdLevel}
                maxLevel={4}
                showChevron
                crowdLabel={dictionary?.crowd ?? 'CROWD'}
                isOpen={isOpen}
              />
            )}
          </div>
        )}

      {/* Mobile view (opening hours) — shown inside fold-out menu when showMobileCrowd is false */}
      {!showMobileCrowd && (
        <div className="lg:hidden">
          <div className="w-full bg-background-primary-light p-4 rounded-lg">
            <div className="flex flex-col gap-2">
              <div className="flex flex-col">
                <div
                  className="font-title font-bold text-text-default"
                  style={{ fontSize: 14, lineHeight: '18px' }}
                >
                  {meetingPlace?.name}
                </div>
                <span
                  className="text-text-default"
                  style={{ fontSize: 12, lineHeight: '18px' }}
                >
                  <OpeningHours
                    openingHoursClosed={dictionary?.openingHoursClosed ?? ''}
                    openingHoursClosedToday={
                      dictionary?.openingHoursClosedToday ?? ''
                    }
                    openingHoursOpenToday={
                      dictionary?.openingHoursOpenToday ?? ''
                    }
                    openingHours={meetingPlace?.openingHours}
                    openingHoursExceptions={
                      meetingPlace?.openingHoursExceptions
                    }
                    timezone={meetingPlace?.timezone}
                    node_locale={meetingPlace?.sys?.locale}
                    variant="today"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop: full opening hours card */}
      <div
        className="hidden lg:block"
        style={{ width: 'calc(50% - 0.625rem)' }}
      >
        {(() => {
          const card = (
            <div className="w-full bg-background-primary-light p-4 lg:hover:bg-background-secondary lg:rounded-[20px]">
              <div className="flex flex-col gap-2">
                <div className="flex justify-between items-start">
                  <div className="flex flex-col">
                    <div
                      className="font-title font-bold text-text-default"
                      style={{ fontSize: 14, lineHeight: '18px' }}
                    >
                      {meetingPlace?.name}
                    </div>
                    <span
                      className="whitespace-nowrap text-text-default"
                      style={{ fontSize: 12, lineHeight: '18px' }}
                    >
                      <OpeningHours
                        openingHoursClosed={
                          dictionary?.openingHoursClosed ?? ''
                        }
                        openingHoursClosedToday={
                          dictionary?.openingHoursClosedToday ?? ''
                        }
                        openingHoursOpenToday={
                          dictionary?.openingHoursOpenToday ?? ''
                        }
                        openingHours={meetingPlace?.openingHours}
                        openingHoursExceptions={
                          meetingPlace?.openingHoursExceptions
                        }
                        timezone={meetingPlace?.timezone}
                        node_locale={meetingPlace?.sys?.locale}
                        variant="today"
                      />
                    </span>
                  </div>
                </div>
                {showCrowdIndicator && crowdLevel !== null && (
                  <CrowdIndicator
                    level={crowdLevel}
                    maxLevel={4}
                    crowdLabel={dictionary?.crowd ?? 'CROWD'}
                    isOpen={isOpen}
                  />
                )}
              </div>
            </div>
          );
          return openingHoursLink ? (
            <LinkWrapper data={openingHoursLink} className="block w-full">
              {card}
            </LinkWrapper>
          ) : (
            card
          );
        })()}
      </div>
    </>
  );
};
